import { useEffect, useState, useCallback } from "react";
import { CaretLeft, CaretRight, Plus } from "@phosphor-icons/react";
import api from "@/lib/api";
import AppointmentDialog from "@/components/AppointmentDialog";
import { useI18n } from "@/i18n/I18nContext";

const HOURS = Array.from({ length: 11 }, (_, i) => i + 9); // 9..19

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // Monday=0
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}

export default function CalendarPage() {
  const { t, lang } = useI18n();
  const loc = lang === "es" ? "es-ES" : "en-US";
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [appts, setAppts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [dialog, setDialog] = useState({ open: false, initial: null, date: null });

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const load = useCallback(async () => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 7);
    const { data } = await api.get("/appointments", {
      params: { start: weekStart.toISOString(), end: end.toISOString() },
    });
    setAppts(data);
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/staff").then(({ data }) => setStaff(data));
    api.get("/services").then(({ data }) => setServices(data));
  }, []);

  const shift = (n) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + n * 7);
    setWeekStart(d);
  };

  const apptsFor = (day, hour) =>
    appts.filter((a) => {
      const s = new Date(a.start);
      return s.toDateString() === day.toDateString() && s.getHours() === hour && a.status !== "cancelled";
    });

  const openNew = (day, hour) => {
    const d = new Date(day);
    setDialog({ open: true, initial: null, date: d.toISOString().slice(0, 10) });
  };

  const today = new Date().toDateString();
  const rangeLabel = `${days[0].toLocaleDateString(loc, { month: "short", day: "numeric" })} – ${days[6].toLocaleDateString(loc, { month: "short", day: "numeric" })}`;

  return (
    <div className="fade-up space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="label-caps">{t.cal.overline}</p>
          <h1 className="font-heading text-3xl font-light tracking-tight mt-1">{t.cal.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-white border border-[#E5E2DC] rounded-lg p-1">
            <button data-testid="cal-prev" onClick={() => shift(-1)} className="p-1.5 rounded-md hover:bg-[#E8E4DF]"><CaretLeft size={16} /></button>
            <button data-testid="cal-today" onClick={() => setWeekStart(startOfWeek(new Date()))} className="px-3 text-sm font-medium">{rangeLabel}</button>
            <button data-testid="cal-next" onClick={() => shift(1)} className="p-1.5 rounded-md hover:bg-[#E8E4DF]"><CaretRight size={16} /></button>
          </div>
          <button data-testid="cal-new-appt" onClick={() => setDialog({ open: true, initial: null, date: new Date().toISOString().slice(0, 10) })}
            className="px-4 py-2.5 rounded-lg bg-[#2C3E35] text-white text-sm font-medium hover:bg-[#1E2B25] transition-colors inline-flex items-center gap-2">
            <Plus size={16} weight="bold" /> {t.cal.new}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2DC] overflow-x-auto">
        <div className="min-w-[820px]">
          <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-[#E5E2DC]">
            <div />
            {days.map((d, i) => (
              <div key={i} className={`px-2 py-3 text-center border-l border-[#E5E2DC] ${d.toDateString() === today ? "bg-[#2C3E35]/5" : ""}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#6B6A67]">{d.toLocaleDateString(loc, { weekday: "short" })}</div>
                <div className={`font-heading text-lg ${d.toDateString() === today ? "text-[#2C3E35] font-bold" : "font-medium"}`}>{d.getDate()}</div>
              </div>
            ))}
          </div>

          {HOURS.map((hour) => (
            <div key={hour} className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-[#E5E2DC] last:border-0">
              <div className="px-2 py-2 text-right text-[11px] text-[#6B6A67]">{hour}:00</div>
              {days.map((day, i) => {
                const items = apptsFor(day, hour);
                return (
                  <div key={i} onClick={() => openNew(day, hour)}
                    className="border-l border-[#E5E2DC] min-h-[58px] p-1 hover:bg-[#FDFBF9] cursor-pointer transition-colors group relative">
                    {items.map((a) => (
                      <div key={a.id} data-testid={`cal-appt-${a.id}`}
                        onClick={(e) => { e.stopPropagation(); setDialog({ open: true, initial: a, date: null }); }}
                        className="mb-1 rounded-md px-2 py-1 text-white text-[11px] leading-tight cursor-pointer hover:opacity-90 transition-opacity"
                        style={{ background: a.staff_color }}>
                        <div className="font-semibold truncate">{a.customer_name}</div>
                        <div className="opacity-85 truncate">{a.service_name}</div>
                      </div>
                    ))}
                    {items.length === 0 && (
                      <div className="opacity-0 group-hover:opacity-100 flex items-center justify-center h-full text-[#cfc9bf] transition-opacity"><Plus size={14} /></div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <AppointmentDialog
        open={dialog.open} initial={dialog.initial} defaultDate={dialog.date}
        onClose={() => setDialog({ open: false, initial: null, date: null })}
        onSaved={load} customers={customers} staff={staff} services={services}
      />
    </div>
  );
}
